import { io, Socket } from 'socket.io-client';
import {
  User,
  Message,
  Channel,
  UserStatusPayload,
  OnlineUser,
  ChannelJoinPayload,
  ChannelReadPayload,
  MessagePayload,
  MessageReadPayload,
  MessageDeletedPayload,
  MessageEditedPayload,
  MessageLinkPreviewPayload,
  MessageCalendarCardPayload,
  MessageNotifyPayload,
  MessageNotifyBatchPayload,
  MentionInput,
  ScheduledMessage,
  ReactionUpdatedPayload,
  ChannelNoticeChangedPayload,
  ChannelLastMessagePayload,
  TypingPayload,
  MemberAddedPayload,
  MemberRemovedPayload,
  CallInitiatePayload,
  CallParticipantPayload,
  RtcSignalPayload,
  ChannelCallStatus,
  CallRingingPayload,
  CallChatMessage,
  NotificationPrivacy,
  PresenceManual,
} from '../types';
import { dispatchUnauthorized } from './apiClient';
import { getAppVersion } from './appVersion';
import { SERVER_BASE_URL } from '../utils/serverUrl';
import { isTauri } from '../utils/tauri';

// 서버 주소 계산은 utils/serverUrl 하나로 통일한다
const SERVER_URL = SERVER_BASE_URL;

/**
 * 원격지원(rs:*) 이벤트 — 콜백 20여 개를 App.tsx에 늘어놓지 않고 RemoteSupportRoot가 직접 구독한다.
 * 'rs:__connect'는 (재)연결 알림이다(끊긴 사이의 세션은 서버가 이미 끝냈다).
 */
export const REMOTE_SUPPORT_EVENTS = [
  'rs:incoming', 'rs:handled', 'rs:accepted', 'rs:started', 'rs:signal', 'rs:info', 'rs:status',
  'rs:control:requested', 'rs:control:granted', 'rs:control:denied', 'rs:control:revoked',
  'rs:input', 'rs:ended', 'config:remote-support', 'remote-support:me-changed',
  // 직원이 먼저 요청한 경우(담당자 쪽): 수락 카드 / 카드 닫힘(다른 기기 수락·취소·만료)
  'rs:ask:incoming', 'rs:ask:handled',
] as const;
export type RemoteSupportEventName = (typeof REMOTE_SUPPORT_EVENTS)[number] | 'rs:__connect';
export type RemoteSupportHandler = (event: RemoteSupportEventName, payload: any) => void;

// ============================================
// 이벤트 콜백 타입 정의
// ============================================

interface SocketCallbacks {
  // 사용자 상태
  onUserStatusChanged?: (payload: UserStatusPayload) => void;
  onOnlineList?: (users: OnlineUser[]) => void;
  onDuplicateLogin?: () => void;
  onUserAvatarChanged?: (payload: { userId: string; avatarUrl: string }) => void;
  /**
   * 알림에 표시할 내용 수준이 바뀌었다. 계정 전체 설정이라 PUT /api/profile/notification-privacy가
   * 그 계정의 전 세션(toUser)에 보낸다 — 다른 기기에서 바꾼 선택이 여기로 따라 들어온다.
   */
  onNotifyPrivacyChanged?: (payload: { privacy: NotificationPrivacy }) => void;
  /**
   * 내가 직접 고른 접속 상태가 바뀌었다(null = 자동 판정). 계정 단위라 PUT /api/profile/presence가
   * 그 계정의 전 세션(toUser)에 보내고, 접속 직후에도 서버가 현재 값을 1회 보내 준다 —
   * 다른 기기가 켜 둔 선택을 새 세션이 알 유일한 경로다.
   * 표시용 상태(online/away/busy)는 이것과 별개로 onUserStatusChanged로 들어온다.
   */
  onPresenceManualChanged?: (payload: { manual: PresenceManual }) => void;
  /**
   * 이용 고지·동의 기록이 바뀌었다(POST /api/consent/chat-notice → 그 계정의 전 세션). accept면 다른 기기의
   * 동의 창이 닫히고, decline·withdraw면 로그아웃한다. ⚠ user 룸은 사번 기준이라 affiliateId로 거른다.
   */
  onConsentUpdated?: (payload: { affiliateId?: string; action: string; consent?: { required: boolean; reason: string } }) => void;
  /**
   * 관리자가 내 로그인 승인을 해제했다. 이 계정의 모든 세션이 대상이며 즉시 로그아웃해야 한다.
   * (오프라인이던 기기는 이 이벤트를 못 받는 대신 다음 /auth/refresh에서 401로 걸린다.)
   */
  onAuthRevoked?: (payload: { reason?: string; code?: string }) => void;
  /** 내 역할/관리자 권한이 바뀌었다. JWT에 박혀 있어 재로그인 전까지는 적용되지 않는다. */
  onUserRoleChanged?: (payload: { userId: string; role: string | null }) => void;
  onUserAdminChanged?: (payload: { userId: string; isAdmin: boolean }) => void;
  /** 관리자가 첨부 용량·개수 제한을 바꿨다. PUT /api/admin/upload-policy가 전 세션에 전파한다. */
  onUploadPolicyChanged?: (payload: { maxFileSizeMB: number; maxFiles: number }) => void;
  onBlockedExtensionsChanged?: (payload: { extensions: Array<{ extension: string }> }) => void;

  // 채널
  onChannelUserJoined?: (payload: ChannelJoinPayload) => void;
  onChannelUserLeft?: (payload: ChannelJoinPayload) => void;
  onChannelNew?: (payload: { channel: Channel }) => void;
  // nameAuto: 그룹방이면 실린다(true = 자동 이름 — updates.name은 받는 사람 기준이고 사람마다 다르다)
  onChannelUpdated?: (payload: { channelId: number; updates: Partial<Channel>; updatedBy?: string; nameAuto?: boolean }) => void;
  onChannelArchived?: (payload: { channelId: number }) => void;
  /**
   * 다른 사용자가 이 채널을 읽었다 (채널 레벨).
   * 현재 App.tsx는 등록하지 않는다 — 메시지별 읽음 표시는 onMessageRead(`message:read`)가
   * 더 정확한 unreadCounts와 함께 처리하고, 타인의 읽음은 내 안읽음 뱃지와 무관하기 때문.
   * 내 읽음 동기화는 onChannelSelfRead를 쓸 것.
   */
  onChannelRead?: (payload: ChannelReadPayload) => void;
  /**
   * 내가(같은 계정의 어느 세션이든) 채널을 읽었다.
   * onChannelRead는 '다른 사용자가 읽었다'는 뜻이라 의미가 다르므로 별도 이벤트로 받는다.
   */
  onChannelSelfRead?: (payload: {
    channelId: number;
    userId: string;
    lastReadMessageId: number;
    unreadCount: number;
    readAt: string;
    originSocketId: string | null;
  }) => void;
  /** 접속·재연결 시 서버가 보내는 채널 목록 전체 스냅샷 */
  onChannelListSync?: (payload: { channels: Channel[] }) => void;
  onChannelPinChanged?: (payload: { channelId: number; isPinned: boolean; pinnedAt: string | null }) => void;
  onChannelMuteChanged?: (payload: { channelId: number; isMuted: boolean }) => void;
  /** 채널 공지 등록/해제 (notice null이면 해제) — REST 라우트가 발생시킴 */
  onChannelNoticeChanged?: (payload: ChannelNoticeChangedPayload) => void;
  /** 마지막 메시지 삭제 시 서버가 보내는 미리보기 정정 (단조성 가드 우회 대상) */
  onChannelLastMessage?: (payload: ChannelLastMessagePayload) => void;

  // 예약 메시지 — 전부 내 계정의 전 세션 대상(realtime.toUser). REST 라우트가
  // created/updated/canceled를, 발송 워커가 sent/failed를 보낸다 (서버 권위 전파)
  onScheduledMessageCreated?: (payload: ScheduledMessage) => void;
  onScheduledMessageUpdated?: (payload: ScheduledMessage) => void;
  onScheduledMessageCanceled?: (payload: ScheduledMessage) => void;
  onScheduledMessageSent?: (payload: ScheduledMessage) => void;
  onScheduledMessageFailed?: (payload: ScheduledMessage) => void;

  // 메시지
  onMessageRead?: (payload: MessageReadPayload) => void;
  onMessageNew?: (message: MessagePayload) => void;
  onMessageNotify?: (payload: MessageNotifyPayload) => void;
  onMessageNotifyBatch?: (payload: MessageNotifyBatchPayload) => void;
  onMessageDeleted?: (payload: MessageDeletedPayload) => void;
  onMessageEdited?: (payload: MessageEditedPayload) => void;
  /** 링크 미리보기 수집 완료 (서버 워커가 보낸다) */
  onMessageLinkPreview?: (payload: MessageLinkPreviewPayload) => void;
  /** 공유한 일정이 삭제됨 — 그 방의 일정 카드를 '삭제된 일정'으로 */
  onMessageCalendarCard?: (payload: MessageCalendarCardPayload) => void;
  onMessageError?: (payload: { error: string }) => void;
  onReactionUpdated?: (payload: ReactionUpdatedPayload) => void;

  // 타이핑
  onTypingStarted?: (payload: TypingPayload) => void;
  onTypingStopped?: (payload: TypingPayload) => void;

  // 멤버
  onMemberAdded?: (payload: MemberAddedPayload) => void;
  onMemberRemoved?: (payload: MemberRemovedPayload) => void;

  /** 채널 입장 실패 (권한 없음, 삭제된 채널 등) — 지금까지 클릭해도 아무 반응이 없었다 */
  onChannelJoinError?: (payload: { channelId: number; error: string }) => void;

  // 감사 알림
  onAuditNotification?: (payload: { message: string; reason: string; auditLogId: number; channelId?: number }) => void;

  // 화상 통화
  onCallIncoming?: (payload: CallInitiatePayload) => void;
  onCallAccepted?: (payload: CallParticipantPayload) => void;
  onCallRejected?: (payload: { channelId: number; callId: string; userId: string; userName: string; channelType?: string }) => void;
  /**
   * 내가(같은 계정의 어느 세션이든) 이 통화를 수락하거나 거절했다.
   * 다른 세션의 착신벨을 끄는 용도 — bySocketId가 내 소켓이면 이미 처리한 것이라 무시한다.
   */
  onCallHandled?: (payload: {
    callId: string;
    channelId: number;
    /** timeout = 서버의 벨 시간 제한(받지 않은 사람의 벨을 끈다) */
    handledBy: 'accept' | 'reject' | 'timeout';
    bySocketId: string | null;
  }) => void;
  /** 통화 실패 사유 (비멤버, 세션 없음 등) — 지금까지 완전히 무음이었다 */
  onCallError?: (payload: { reason: string; callId?: string }) => void;
  onCallEnded?: (payload: { channelId: number; callId: string; endedBy: string; reason?: string }) => void;
  onCallParticipantJoined?: (payload: CallParticipantPayload) => void;
  onCallParticipantLeft?: (payload: { channelId: number; callId: string; userId: string; reason?: string }) => void;
  onRtcSignal?: (payload: RtcSignalPayload) => void;
  /** 끊긴 상대가 다시 offer해 달라고 청했다(여러 명 통화 재연결) */
  onCallReconnectRequest?: (payload: { callId: string; fromUserId: string }) => void;
  /** 대화방에서 진행 중인 통화가 바뀌었다('통화 중 · 참여' 바) */
  onCallStatus?: (payload: ChannelCallStatus) => void;
  /** 통화 중 초대로 벨이 울리는 사람이 바뀌었다(게스트가 아닌 참가자에게만 온다) */
  onCallRinging?: (payload: CallRingingPayload) => void;
  onCallRecordingChanged?: (payload: { callId: string; userId: string; userName: string; isRecording: boolean }) => void;
  /** 통화 채팅 한 건(내가 보낸 것은 ack로 받으므로 오지 않는다) */
  onCallChatMessage?: (payload: { callId: string; message: CallChatMessage }) => void;

  // 연결 상태
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onReconnect?: (attemptNumber: number) => void;
  onError?: (error: Error) => void;

  // 익명 알림 (Tauri 로그아웃 후 device 기반 수신)
  onAnonymousNotify?: (payload: { channelId: number }) => void;
}

// ============================================
// Socket Service 클래스
// ============================================

class SocketService {
  private socket: Socket | null = null;
  private notifySocket: Socket | null = null;
  private callbacks: SocketCallbacks = {};
  /** 원격지원 이벤트 구독자 — 로그아웃(disconnect)에도 지우지 않는다(구독한 컴포넌트가 언마운트 때 스스로 푼다) */
  private rsHandlers = new Set<RemoteSupportHandler>();
  private currentUser: { userId: string; userName: string; avatar?: string; affiliateId?: string; depart?: string } | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;

  // ----------------------------------------
  // 연결 관리
  // ----------------------------------------

  /**
   * 소켓 연결 및 사용자 온라인 등록
   */
  connect(user: User): Socket {
    if (this.socket) {
      if (!this.socket.connected) {
        this.socket.connect();
      }
      return this.socket;
    }

    this.currentUser = {
      userId: String(user.id),
      userName: user.username,
      avatar: user.avatar,
      affiliateId: user.affiliateId,
      depart: user.depart,
    };

    this.socket = io(SERVER_URL, {
      // 함수형 auth: 재연결 시마다 호출되므로 슬라이딩 갱신된 최신 토큰이 항상 쓰인다
      // (객체형은 최초 연결 시점의 토큰이 고정되어 갱신 후 재연결이 만료 토큰으로 실패)
      auth: (cb) => cb({ token: localStorage.getItem('authToken') }),
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      // 서버 재시작 후 전 클라이언트가 같은 순간 몰려 재접속하지 않도록 상한을 넓힌다(±50% 지터 → 4~12s).
      // 첫 재시도(1s)는 그대로라 순간 끊김 복구 체감은 같다. 부하 테스트 S2-A vs S2-B: 5초 버스트와
      // 30초 분산의 목록 표시 지연이 40배 차이 났다(docs/loadtest-2026-08.md §5.2-2).
      reconnectionDelayMax: 8000,
      randomizationFactor: 0.5,
      timeout: 20000,
    });

    this.setupEventListeners();
    // registerUser()는 'connect' 이벤트에서 호출됨 (중복 호출 방지)

    return this.socket;
  }

  /** 서버가 끊은 연결(세션 만료)을 새 토큰으로 다시 잇는다 — 함수형 auth가 최신 토큰을 읽는다 */
  reconnectNow(): void {
    if (this.socket && !this.socket.connected) this.socket.connect();
  }

  /**
   * refresh/resume로 받은 새 토큰을 접속 중인 소켓에 올린다. 서버 소켓 스윕(기기 신뢰 enforce)은
   * 핸드셰이크 토큰의 만료 시각을 보므로, 이게 없으면 토큰을 갱신해도 옛 만료 시각에 끊긴다.
   * 구 서버는 핸들러가 없어 무시한다.
   */
  pushToken(token: string): void {
    if (this.socket?.connected && token) this.socket.emit('auth:token', { token });
  }

  /**
   * 소켓 연결 해제
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.currentUser = null;
      this.callbacks = {};
    }
  }

  /**
   * 익명 알림 전용 socket 연결 (Tauri 로그아웃 후 device 기반)
   * 일반 socket과 분리된 별도 인스턴스. 메시지 송수신 불가, notify:anonymous 이벤트만 수신.
   */
  connectNotifyOnly(deviceId: string): Socket {
    if (this.notifySocket) {
      if (!this.notifySocket.connected) this.notifySocket.connect();
      return this.notifySocket;
    }
    this.notifySocket = io(SERVER_URL, {
      auth: { deviceId, mode: 'notify' },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
      // 서버 재시작 후 전 클라이언트가 같은 순간 몰려 재접속하지 않도록 상한을 넓힌다(±50% 지터 → 4~12s).
      // 첫 재시도(1s)는 그대로라 순간 끊김 복구 체감은 같다. 부하 테스트 S2-A vs S2-B: 5초 버스트와
      // 30초 분산의 목록 표시 지연이 40배 차이 났다(docs/loadtest-2026-08.md §5.2-2).
      reconnectionDelayMax: 8000,
      randomizationFactor: 0.5,
      timeout: 20000,
    });
    this.notifySocket.on('connect', () => {
      console.log('[NotifySocket] Connected:', this.notifySocket?.id);
    });
    this.notifySocket.on('disconnect', (reason) => {
      console.log('[NotifySocket] Disconnected:', reason);
    });
    this.notifySocket.on('notify:anonymous', (payload: { channelId: number }) => {
      this.callbacks.onAnonymousNotify?.(payload);
    });
    return this.notifySocket;
  }

  /**
   * 익명 알림 socket만 해제 (일반 socket은 영향 없음)
   */
  disconnectNotify(): void {
    if (this.notifySocket) {
      this.notifySocket.disconnect();
      this.notifySocket = null;
    }
  }

  /**
   * 연결 상태 확인
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * 소켓 인스턴스 반환
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * 현재 소켓 id. 서버가 "어느 세션이 처리했는지"를 실어 보내는 이벤트(call:handled 등)에서
   * 내가 한 행동인지 구분하는 데 쓴다.
   */
  getSocketId(): string | null {
    return this.socket?.id ?? null;
  }

  // ----------------------------------------
  // 콜백 등록
  // ----------------------------------------

  // ----------------------------------------
  // 원격지원 (rs:*)
  // ----------------------------------------

  private dispatchRs(event: RemoteSupportEventName, payload: any): void {
    this.rsHandlers.forEach((h) => {
      try {
        h(event, payload);
      } catch (err) {
        console.error('[Socket] remote support handler failed:', err);
      }
    });
  }

  /** @returns 구독 해제 */
  subscribeRemoteSupport(handler: RemoteSupportHandler): () => void {
    this.rsHandlers.add(handler);
    return () => {
      this.rsHandlers.delete(handler);
    };
  }

  emitRemoteSupport(event: string, payload: unknown): void {
    this.socket?.emit(event, payload);
  }

  /** 통화 이벤트 — callController가 쓴다(신원은 서버가 소켓에서 정한다) */
  emitCall(event: string, payload: unknown): void {
    this.socket?.emit(event, payload);
  }

  /** 통화 ack emit. 연결이 없으면 { ok:false, code:'OFFLINE' }, 시간 안에 답이 없으면 { ok:false, code:'TIMEOUT' } */
  emitCallWithAck(event: string, payload: unknown, timeoutMs = 10000): Promise<{ ok: boolean; code?: string; [k: string]: any }> {
    const socket = this.socket;
    if (!socket || !socket.connected) return Promise.resolve({ ok: false, code: 'OFFLINE' });
    return new Promise((resolve) => {
      socket.timeout(timeoutMs).emit(event, payload, (err: Error | null, res: any) => {
        if (err || !res || typeof res !== 'object') resolve({ ok: false, code: 'TIMEOUT' });
        else resolve(res);
      });
    });
  }

  /** ack를 받는 emit. 연결이 없거나 시간 안에 답이 없으면 { ok:false } */
  emitRemoteSupportWithAck<T extends { ok: boolean } = { ok: boolean; message?: string }>(
    event: string,
    payload: unknown,
    timeoutMs = 10000
  ): Promise<T> {
    const socket = this.socket;
    if (!socket || !socket.connected) {
      return Promise.resolve({ ok: false, message: '서버에 연결되어 있지 않습니다. 잠시 후 다시 시도해 주세요.' } as unknown as T);
    }
    return new Promise((resolve) => {
      socket.timeout(timeoutMs).emit(event, payload, (err: Error | null, res: T) => {
        if (err || !res) {
          resolve({ ok: false, message: '서버가 응답하지 않습니다. 잠시 후 다시 시도해 주세요.' } as unknown as T);
        } else {
          resolve(res);
        }
      });
    });
  }

  /**
   * 이벤트 콜백 등록
   */
  setCallbacks(callbacks: Partial<SocketCallbacks>): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * 특정 콜백 제거
   */
  removeCallback(key: keyof SocketCallbacks): void {
    delete this.callbacks[key];
  }

  /**
   * 모든 콜백 제거
   */
  clearCallbacks(): void {
    this.callbacks = {};
  }

  // ----------------------------------------
  // 이벤트 리스너 설정
  // ----------------------------------------

  private setupEventListeners(): void {
    if (!this.socket) return;

    // 연결 상태
    this.socket.on('connect', () => {
      console.log('[Socket] Connected:', this.socket?.id);
      this.reconnectAttempts = 0;
      this.registerUser();
      this.callbacks.onConnect?.();
      this.dispatchRs('rs:__connect', null);
    });

    for (const event of REMOTE_SUPPORT_EVENTS) {
      this.socket.on(event, (payload: any) => this.dispatchRs(event, payload));
    }

    this.socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
      this.callbacks.onDisconnect?.(reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error);
      // CR-1: 서버가 JWT를 거부하면 자동 재로그인 트리거
      // 기기 신뢰: 'UNAUTHORIZED: DEVICE_REVOKED' 처럼 이유 코드가 붙는다 — App이 문구·재개 시도를 고른다.
      // 'SERVICE_UNAVAILABLE: …'(서버 기기 캐시 준비 전)은 로그아웃시키지 않고 재연결을 계속한다.
      if (error?.message && /UNAUTHORIZED/i.test(error.message)) {
        const code = /UNAUTHORIZED:\s*([A-Z_]+)/.exec(error.message)?.[1];
        dispatchUnauthorized(code && (code.startsWith('DEVICE_') || code === 'SESSION_REVOKED') ? code : undefined);
      }
      this.callbacks.onError?.(error);
    });

    this.socket.io.on('reconnect', (attemptNumber) => {
      console.log('[Socket] Reconnected after', attemptNumber, 'attempts');
      this.callbacks.onReconnect?.(attemptNumber);
      // 끊긴 동안의 calendar:changed는 유실됐다 — 캘린더 캐시가 통째로 다시 받게 한다
      window.dispatchEvent(new CustomEvent('socket:reconnected'));
    });

    // 사용자 상태
    this.socket.on('user:status-changed', (payload: UserStatusPayload) => {
      this.callbacks.onUserStatusChanged?.(payload);
    });
    // 배치(서버 STATUS_BATCH_MS 창에 모인 변경). 건별 콜백으로 풀어 주면 App.tsx는 무변경이고,
    // React 18 자동 배칭이 같은 틱의 setState를 한 번에 커밋한다.
    this.socket.on('user:status-changed-batch', (payload: { changes?: UserStatusPayload[] }) => {
      const changes = Array.isArray(payload?.changes) ? payload.changes : [];
      for (const change of changes) this.callbacks.onUserStatusChanged?.(change);
    });

    this.socket.on('user:online-list', (users: OnlineUser[]) => {
      this.callbacks.onOnlineList?.(users);
    });

    this.socket.on('user:duplicate-login', () => {
      console.warn('[Socket] Duplicate login detected');
      this.callbacks.onDuplicateLogin?.();
    });

    this.socket.on('user:avatar-changed', (payload: { userId: string; avatarUrl: string }) => {
      this.callbacks.onUserAvatarChanged?.(payload);
    });

    // 알림 내용 공개 수준 변경 — PUT /api/profile/notification-privacy가 내 전 세션에 보낸다
    this.socket.on('user:notify-privacy-changed', (payload: { privacy: NotificationPrivacy }) => {
      this.callbacks.onNotifyPrivacyChanged?.(payload);
    });

    // 내 수동 상태 — PUT /api/profile/presence(내 전 세션) + 접속 직후 현재 값 1회
    this.socket.on('user:presence-manual', (payload: { manual?: PresenceManual }) => {
      this.callbacks.onPresenceManualChanged?.({ manual: payload?.manual ?? null });
    });

    // 로그인 승인 해제 — DELETE /api/admin/login-approvals/:affiliateId/:userId가 보낸다
    // 기기 승인 해제(code:'DEVICE_REVOKED')도 같은 이벤트다 — 서버가 곧바로 이 소켓을 끊는다
    this.socket.on('auth:revoked', (payload: { reason?: string; code?: string }) => {
      console.warn('[Socket] Session revoked', payload?.code || '');
      this.callbacks.onAuthRevoked?.(payload || {});
    });
    // 기기 신뢰(enforce): 소켓에 올린 토큰이 만료됐다(서버가 곧 끊는다). 서버가 끊은 연결은 자동 재연결되지
    // 않으므로 App이 토큰을 갱신한 뒤 reconnectNow()를 부른다.
    this.socket.on('auth:session-expired', () => {
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
    });
    // 기기 승인 대기 목록이 바뀌었다 / 직원이 "내 요청이 아닙니다"로 신고했다 — super_admin 세션에만 온다.
    // 관리자 콘솔 '기기 승인' 탭(목록·배지)이 window 이벤트로 받는다
    this.socket.on('device:pending-changed', () => {
      window.dispatchEvent(new CustomEvent('device:pending-changed'));
    });
    this.socket.on('device:reported', (payload: { userName?: string; deviceId?: number }) => {
      window.dispatchEvent(new CustomEvent('device:reported', { detail: payload || {} }));
    });

    // 채널
    this.socket.on('channel:user-joined', (payload: ChannelJoinPayload) => {
      this.callbacks.onChannelUserJoined?.(payload);
    });

    this.socket.on('channel:user-left', (payload: ChannelJoinPayload) => {
      this.callbacks.onChannelUserLeft?.(payload);
    });

    this.socket.on('channel:new', (payload: { channel: Channel }) => {
      this.callbacks.onChannelNew?.(payload);
    });

    this.socket.on('channel:info-updated', (payload) => {
      this.callbacks.onChannelUpdated?.(payload);
    });

    this.socket.on('channel:archived', (payload: { channelId: number }) => {
      this.callbacks.onChannelArchived?.(payload);
    });

    this.socket.on('channel:read', (payload: ChannelReadPayload) => {
      this.callbacks.onChannelRead?.(payload);
    });

    this.socket.on('channel:self-read', (payload: any) => {
      this.callbacks.onChannelSelfRead?.(payload);
    });

    this.socket.on('channel:list-sync', (payload: { channels: Channel[] }) => {
      this.callbacks.onChannelListSync?.(payload);
    });

    this.socket.on('channel:pin-changed', (payload: { channelId: number; isPinned: boolean; pinnedAt: string | null }) => {
      this.callbacks.onChannelPinChanged?.(payload);
    });

    this.socket.on('channel:mute-changed', (payload: { channelId: number; isMuted: boolean }) => {
      this.callbacks.onChannelMuteChanged?.(payload);
    });

    this.socket.on('channel:notice-changed', (payload: ChannelNoticeChangedPayload) => {
      this.callbacks.onChannelNoticeChanged?.(payload);
    });

    this.socket.on('channel:last-message', (payload: ChannelLastMessagePayload) => {
      this.callbacks.onChannelLastMessage?.(payload);
    });

    // 예약 메시지 (내 계정 전 세션 동기화)
    this.socket.on('scheduled-message:created', (payload: ScheduledMessage) => {
      this.callbacks.onScheduledMessageCreated?.(payload);
    });
    this.socket.on('scheduled-message:updated', (payload: ScheduledMessage) => {
      this.callbacks.onScheduledMessageUpdated?.(payload);
    });
    this.socket.on('scheduled-message:canceled', (payload: ScheduledMessage) => {
      this.callbacks.onScheduledMessageCanceled?.(payload);
    });
    this.socket.on('scheduled-message:sent', (payload: ScheduledMessage) => {
      this.callbacks.onScheduledMessageSent?.(payload);
    });
    this.socket.on('scheduled-message:failed', (payload: ScheduledMessage) => {
      this.callbacks.onScheduledMessageFailed?.(payload);
    });

    // 메시지
    this.socket.on('message:read', (payload: MessageReadPayload) => {
      this.callbacks.onMessageRead?.(payload);
    });

    this.socket.on('message:new', (message: MessagePayload) => {
      this.callbacks.onMessageNew?.(message);
    });

    this.socket.on('message:notify', (payload: MessageNotifyPayload) => {
      this.callbacks.onMessageNotify?.(payload);
    });

    this.socket.on('message:notify-batch', (payload: MessageNotifyBatchPayload) => {
      this.callbacks.onMessageNotifyBatch?.(payload);
    });

    this.socket.on('message:deleted', (payload: MessageDeletedPayload) => {
      this.callbacks.onMessageDeleted?.(payload);
    });

    this.socket.on('message:edited', (payload: MessageEditedPayload) => {
      this.callbacks.onMessageEdited?.(payload);
    });

    this.socket.on('message:link-preview', (payload: MessageLinkPreviewPayload) => {
      this.callbacks.onMessageLinkPreview?.(payload);
    });

    this.socket.on('message:calendar-card', (payload: MessageCalendarCardPayload) => {
      this.callbacks.onMessageCalendarCard?.(payload);
    });

    this.socket.on('message:error', (payload: { error: string }) => {
      this.callbacks.onMessageError?.(payload);
    });

    this.socket.on('message:reaction:updated', (payload: ReactionUpdatedPayload) => {
      this.callbacks.onReactionUpdated?.(payload);
    });

    // 타이핑
    this.socket.on('typing:started', (payload: TypingPayload) => {
      this.callbacks.onTypingStarted?.(payload);
    });

    this.socket.on('typing:stopped', (payload: TypingPayload) => {
      this.callbacks.onTypingStopped?.(payload);
    });

    // 멤버
    this.socket.on('member:added', (payload: MemberAddedPayload) => {
      this.callbacks.onMemberAdded?.(payload);
    });

    this.socket.on('member:removed', (payload: MemberRemovedPayload) => {
      this.callbacks.onMemberRemoved?.(payload);
    });

    // 감사 알림
    this.socket.on('audit:notification', (payload) => {
      this.callbacks.onAuditNotification?.(payload);
      // CustomEvent로 전파 (AuditNotificationBanner에서 수신)
      window.dispatchEvent(new CustomEvent('audit:notification', { detail: payload }));
    });

    // 내가 다른 기기에서 감사 알림을 읽음 처리했다 → 이 기기 배너에서도 지운다
    this.socket.on('audit:notification-read', (payload) => {
      window.dispatchEvent(new CustomEvent('audit:notification-read', { detail: payload }));
    });

    // 대화 내용 열람 승인 — 요청 목록이 바뀌었다(Super Admin) / 내 요청이 결정됐다(감사자).
    // 관리자 콘솔 '메시지 감사'·'한눈에 보기'가 window 이벤트로 독립 구독한다(App 콜백을 늘리지 않는다)
    this.socket.on('audit:access-requested', (payload) => {
      window.dispatchEvent(new CustomEvent('audit:access-changed', { detail: payload || {} }));
    });
    this.socket.on('audit:access-decided', (payload) => {
      window.dispatchEvent(new CustomEvent('audit:access-changed', { detail: payload || {} }));
    });

    // 이용 고지·동의 — routes/consent.js가 내 전 세션에 보낸다
    this.socket.on('consent:updated', (payload) => {
      this.callbacks.onConsentUpdated?.(payload);
    });

    // 통합 알림함 (Phase 2) — CustomEvent로만 전파 (ActivityPanel·App 뱃지가 독립 구독,
    // SocketCallbacks 미확장으로 App.tsx 거대 useEffect 비대화 회피)
    this.socket.on('activity:new', (payload) => {
      window.dispatchEvent(new CustomEvent('activity:new', { detail: payload }));
    });

    // 읽음 동기화: {id} 단건 / {all:true} 전체 / {removedId} 리액션 취소 회수
    this.socket.on('activity:read', (payload) => {
      window.dispatchEvent(new CustomEvent('activity:read', { detail: payload }));
    });

    // 일정 캘린더 — 내용 없는 무효화 신호({eventId, scope}). calendarService가 구독해 화면에 떠 있는 범위만 다시 받는다
    this.socket.on('calendar:changed', (payload) => {
      window.dispatchEvent(new CustomEvent('calendar:changed', { detail: payload }));
    });

    // 화상 통화
    this.socket.on('call:incoming', (payload: CallInitiatePayload) => {
      this.callbacks.onCallIncoming?.(payload);
    });

    this.socket.on('call:accepted', (payload: CallParticipantPayload) => {
      this.callbacks.onCallAccepted?.(payload);
    });

    this.socket.on('call:rejected', (payload) => {
      this.callbacks.onCallRejected?.(payload);
    });

    this.socket.on('call:ended', (payload) => {
      this.callbacks.onCallEnded?.(payload);
    });

    this.socket.on('call:handled', (payload: any) => {
      this.callbacks.onCallHandled?.(payload);
    });

    this.socket.on('call:error', (payload: any) => {
      this.callbacks.onCallError?.(payload);
    });

    this.socket.on('channel:join:error', (payload: any) => {
      this.callbacks.onChannelJoinError?.(payload);
    });

    this.socket.on('user:role-changed', (payload: any) => {
      this.callbacks.onUserRoleChanged?.(payload);
    });

    this.socket.on('user:admin-changed', (payload: any) => {
      this.callbacks.onUserAdminChanged?.(payload);
    });

    this.socket.on('config:upload-policy', (payload: any) => {
      this.callbacks.onUploadPolicyChanged?.(payload);
    });

    this.socket.on('config:blocked-extensions', (payload: any) => {
      this.callbacks.onBlockedExtensionsChanged?.(payload);
    });

    this.socket.on('call:participant-joined', (payload: CallParticipantPayload) => {
      this.callbacks.onCallParticipantJoined?.(payload);
    });

    this.socket.on('call:participant-left', (payload) => {
      this.callbacks.onCallParticipantLeft?.(payload);
    });

    this.socket.on('rtc:signal', (payload: RtcSignalPayload) => {
      this.callbacks.onRtcSignal?.(payload);
    });

    this.socket.on('call:reconnect-request', (payload) => {
      this.callbacks.onCallReconnectRequest?.(payload);
    });

    this.socket.on('call:status', (payload: ChannelCallStatus) => {
      this.callbacks.onCallStatus?.(payload);
    });

    this.socket.on('call:ringing', (payload: CallRingingPayload) => {
      this.callbacks.onCallRinging?.(payload);
    });

    this.socket.on('call:recording-changed', (payload) => {
      this.callbacks.onCallRecordingChanged?.(payload);
    });

    this.socket.on('call:chat:message', (payload) => {
      this.callbacks.onCallChatMessage?.(payload);
    });
  }

  private async registerUser(): Promise<void> {
    if (this.socket && this.currentUser) {
      const appVersion = await getAppVersion();
      // capabilities: 서버가 지원 여부에 따라 이벤트 형태를 고른다.
      //  - 'status-batch': user:status-changed를 건별 대신 user:status-changed-batch { changes:[…] }로 모아 받는다
      //    (접속 폭주 시 N² 이벤트 억제). 미선언 구버전은 서버가 건별로 계속 보낸다.
      //  - 'rs-host': 원격지원 화면을 내줄 수 있는 PC 앱(네이티브 동의창·입력 게이트 포함). 웹·모바일은 선언하지 않는다
      //  - 'call-guest': 통화 게스트 벨을 받아 채팅·녹화를 숨길 줄 안다. 미선언(구버전)에게는 서버가 게스트 초대를 보내지 않는다(OUTDATED)
      //  - 'call-chat': 통화 채팅(call:chat:message)을 받아 그린다. 미선언이면 다른 참가자 화면에 '앱을 업데이트해야…'가 뜬다
      const capabilities = isTauri()
        ? ['status-batch', 'rs-host', 'call-guest', 'call-chat']
        : ['status-batch', 'call-guest', 'call-chat'];
      this.socket.emit('user:online', { ...this.currentUser, appVersion, capabilities });
    }
  }

  emitGoingOffline(): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit('user:going-offline');
    }
  }

  emitGoingOnline(): void {
    this.registerUser();
  }

  // ----------------------------------------
  // 사용자 상태 API
  // ----------------------------------------

  /**
   * 사용자 아바타 업데이트 (재연결 시 최신 아바타 전송용)
   */
  updateUserAvatar(avatarUrl: string | null): void {
    if (this.currentUser) {
      this.currentUser.avatar = avatarUrl;
    }
  }

  // 상태를 바꾸는 emit은 두지 않는다. 서버가 값 검증 없이 세션에 대입하던 user:status 핸들러를
  // 제거했고(오프라인 위장이 가능했다), 수동 상태는 PUT /api/profile/presence 하나가 바꾼다 —
  // "상태를 바꾸는 REST 라우트가 소켓 전파의 주체"라는 규칙 그대로.

  // ----------------------------------------
  // 채널 API
  // ----------------------------------------

  /**
   * 채널 입장
   */
  joinChannel(channelId: number): void {
    this.socket?.emit('channel:join', { channelId });
  }

  /**
   * 채널 퇴장
   */
  leaveChannel(channelId: number): void {
    this.socket?.emit('channel:leave', { channelId });
  }

  /**
   * 새 채널 생성 알림
   * @deprecated 서버가 POST /api/channels에서 직접 전파한다. 호출부는 이미 제거됐고,
   * 서버의 레거시 핸들러도 중복이면 무시한다. 구버전 클라이언트가 모두 사라지면 삭제할 것.
   */
  notifyChannelCreated(channel: Channel, memberIds: string[]): void {
    this.socket?.emit('channel:created', { channel, memberIds });
  }

  /**
   * 채널 읽음 표시
   */
  markChannelRead(channelId: number, messageIds?: number[]): void {
    this.socket?.emit('channel:mark-read', { channelId, messageIds });
  }

  /**
   * 채널 포커스 상태 알림 (읽음 처리용)
   */
  emitChannelFocus(channelId: number | null): void {
    if (channelId) {
      this.socket?.emit('channel:focus', { channelId });
    }
  }

  emitChannelBlur(): void {
    this.socket?.emit('channel:blur');
  }

  /**
   * 데스크톱 활동 하트비트 — "PC 사용 중이면 모바일 푸시 억제"의 근거.
   * idleMs = 마지막 OS(Tauri)/페이지(웹) 입력 후 경과 ms. 서버는 자기 임계값(기본 5분)으로 판정한다.
   * locked = 지금 Windows 화면이 잠겨 있는가(Tauri Windows 전용). true면 서버가 idleMs와 무관하게
   *   즉시 "PC 안 씀"으로 보고 모바일 푸시를 재개한다. 엣지가 아니라 '상태'라 매번 실어 보낸다 —
   *   잠금 이벤트를 놓치거나 잠긴 채로 소켓이 재연결돼도 다음 하트비트가 자가 치유한다.
   * 연결이 없으면 조용히 버린다 — 다음 하트비트(30초)나 연결 직후 즉시 전송이 메꾼다.
   */
  emitActivity(idleMs: number, locked = false): void {
    if (!this.socket?.connected) return;
    this.socket.emit('user:activity', { idleMs, locked });
  }

  // ----------------------------------------
  // 메시지 API
  // ----------------------------------------

  /**
   * 메시지 전송
   */
  sendMessage(
    channelId: number,
    content: string,
    msgType: 'text' | 'image' | 'file' = 'text',
    replyToId?: number,
    attachments?: Array<{ id: string; name: string; size: number; type: string; url: string }>,
    mentions?: MentionInput[]
  ): void {
    this.socket?.emit('message:send', {
      channelId,
      content,
      msgType,
      replyToId,
      attachments,
      mentions: mentions && mentions.length > 0 ? mentions : undefined,
    });
  }

  /**
   * 메시지 삭제
   */
  deleteMessage(messageId: number, channelId: number): void {
    this.socket?.emit('message:delete', { messageId, channelId });
  }

  /**
   * 메시지 수정
   */
  editMessage(messageId: number, channelId: number, content: string, mentions?: MentionInput[]): void {
    // mentions는 항상 배열로 보낸다(빈 배열 포함) — 서버가 재인덱스해 제거된 멘션을 반영.
    // undefined로 보내면 서버는 기존 인덱스를 보존한다(구버전 호환 경로).
    this.socket?.emit('message:edit', { messageId, channelId, content, mentions: mentions ?? [] });
  }

  toggleReaction(messageId: number, channelId: number, emoji: string): void {
    this.socket?.emit('message:reaction:toggle', { messageId, channelId, emoji });
  }

  // ----------------------------------------
  // 타이핑 API
  // ----------------------------------------

  private typingTimeouts: Map<number, NodeJS.Timeout> = new Map();

  /**
   * 타이핑 시작 (자동으로 3초 후 중지)
   */
  startTyping(channelId: number): void {
    // 이전 타이머 취소
    const existingTimeout = this.typingTimeouts.get(channelId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    this.socket?.emit('typing:start', { channelId });

    // 3초 후 자동 중지
    const timeout = setTimeout(() => {
      this.stopTyping(channelId);
    }, 3000);

    this.typingTimeouts.set(channelId, timeout);
  }

  /**
   * 타이핑 중지
   */
  stopTyping(channelId: number): void {
    const existingTimeout = this.typingTimeouts.get(channelId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.typingTimeouts.delete(channelId);
    }

    this.socket?.emit('typing:stop', { channelId });
  }

  // ----------------------------------------
  // 멤버 API
  // ----------------------------------------

  /**
   * 멤버 초대 알림
   * @deprecated 서버가 POST /api/channels/:id/members에서 직접 전파한다. 호출부는 이미 제거됐고,
   * 서버의 레거시 핸들러도 중복이면 무시한다. 구버전 클라이언트가 모두 사라지면 삭제할 것.
   */
  notifyMemberInvited(
    channelId: number,
    members: Array<{ id: string; name: string }>,
    channelInfo?: Channel
  ): void {
    this.socket?.emit('member:invited', { channelId, members, channelInfo });
  }

  // ----------------------------------------
  // 화상 통화 API
  // ----------------------------------------

  initiateCall(channelId: number, channelName: string, callId: string, callerName: string, callerAvatar?: string): void {
    this.socket?.emit('call:initiate', {
      channelId,
      channelName,
      callId,
      callerId: this.currentUser?.userId,
      callerName,
      callerAvatar,
    });
  }

  acceptCall(channelId: number, callId: string, userName: string, avatar?: string): void {
    this.socket?.emit('call:accept', {
      channelId,
      callId,
      userId: this.currentUser?.userId,
      userName,
      avatar,
    });
  }

  rejectCall(channelId: number, callId: string): void {
    this.socket?.emit('call:reject', {
      channelId,
      callId,
      userId: this.currentUser?.userId,
    });
  }

  endCall(channelId: number, callId: string): void {
    this.socket?.emit('call:end', {
      channelId,
      callId,
      userId: this.currentUser?.userId,
    });
  }

  sendRtcSignal(toUserId: string, callId: string, signal: any): void {
    this.socket?.emit('rtc:signal', {
      callId,
      fromUserId: this.currentUser?.userId,
      toUserId,
      signal,
    });
  }

  notifyRecordingChanged(callId: string, isRecording: boolean): void {
    this.socket?.emit('call:recording-changed', {
      callId,
      userId: this.currentUser?.userId,
      userName: this.currentUser?.userName,
      isRecording,
    });
  }
}

// 싱글톤 인스턴스 export
export const socketService = new SocketService();

// 기기 신뢰: authService가 새 토큰을 저장할 때마다 소켓에도 올린다(서버 'auth:token')
if (typeof window !== 'undefined') {
  window.addEventListener('auth:token-updated', (e) => {
    const token = (e as CustomEvent<string>).detail;
    if (typeof token === 'string') socketService.pushToken(token);
  });
}

// ============================================
// React Hook (optional)
// ============================================

import { useEffect, useCallback, useState } from 'react';

/**
 * 소켓 연결 및 이벤트 관리 훅
 */
export function useSocket(user: User | null) {
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);

  useEffect(() => {
    if (!user) {
      socketService.disconnect();
      setIsConnected(false);
      return;
    }

    socketService.connect(user);

    socketService.setCallbacks({
      onConnect: () => setIsConnected(true),
      onDisconnect: () => setIsConnected(false),
      onOnlineList: (users) => setOnlineUsers(users),
      onUserStatusChanged: (payload) => {
        setOnlineUsers((prev) => {
          const index = prev.findIndex((u) => u.userId === payload.userId);
          if (payload.status === 'offline') {
            return prev.filter((u) => u.userId !== payload.userId);
          }
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = { ...updated[index], status: payload.status };
            return updated;
          }
          return [...prev, { userId: payload.userId, userName: payload.userName, status: payload.status }];
        });
      },
    });

    return () => {
      socketService.disconnect();
    };
  }, [user]);

  return {
    isConnected,
    onlineUsers,
    socket: socketService,
  };
}

/**
 * 타이핑 표시 훅
 * @param selfUserId 본인 사번 — 서버 typing:started는 발신 소켓만 제외(socket.to)라
 *                   같은 계정의 다른 세션에는 내 타이핑이 전달되므로 여기서 걸러낸다
 */
export function useTypingIndicator(channelId: number | null, selfUserId?: string) {
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    // 채널 전환 시 이전 채널의 타이핑 표시 잔존 방지
    setTypingUsers(new Map());
    if (!channelId) return;

    const timeouts = new Map<string, NodeJS.Timeout>();

    socketService.setCallbacks({
      onTypingStarted: (payload) => {
        if (selfUserId != null && String(payload.userId) === String(selfUserId)) return;
        if (payload.channelId === channelId && payload.userName) {
          setTypingUsers((prev) => new Map(prev).set(payload.userId, payload.userName!));

          // 기존 타이머 클리어
          const existing = timeouts.get(payload.userId);
          if (existing) clearTimeout(existing);

          // 5초 후 자동 제거
          const timeout = setTimeout(() => {
            setTypingUsers((prev) => {
              const next = new Map(prev);
              next.delete(payload.userId);
              return next;
            });
          }, 5000);

          timeouts.set(payload.userId, timeout);
        }
      },
      onTypingStopped: (payload) => {
        if (payload.channelId === channelId) {
          setTypingUsers((prev) => {
            const next = new Map(prev);
            next.delete(payload.userId);
            return next;
          });

          const existing = timeouts.get(payload.userId);
          if (existing) clearTimeout(existing);
        }
      },
    });

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [channelId, selfUserId]);

  const startTyping = useCallback(() => {
    if (channelId) {
      socketService.startTyping(channelId);
    }
  }, [channelId]);

  const stopTyping = useCallback(() => {
    if (channelId) {
      socketService.stopTyping(channelId);
    }
  }, [channelId]);

  return {
    typingUsers: Array.from(typingUsers.values()),
    startTyping,
    stopTyping,
  };
}
